import { useQuery } from '@apollo/client';
import { faPencil, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as R from 'ramda';
import { Link } from 'react-router';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import type { UploadPaleomapListPageQuery } from '~/apollo/generated/v4/graphql';
import { FilterSearch } from '~/components/common/FilterSearch';
import { NoItemsRow } from '~/components/common/NoItemsRow';
import { PageHeading } from '~/components/common/PageHeading';
import { Panel } from '~/components/common/Panel';
import { SortTrigger } from '~/components/common/SortTrigger';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { useSortFilter } from '~/hooks/data';
import * as routes from '~/paths';

const UPLOAD_PALEOMAP_LIST_PAGE = graphql(`
  query UploadPaleomapListPage {
    paleoMapList {
      results {
        ...paleoMapParts
        outcrops {
          id
          name
          region {
            id
            name
          }
        }
        age {
          ...lithostratAgeParts
        }
      }
    }
  }
`);

export default function UploadPaleomapIndexRoute() {
  return (
    <ApolloProviderV4>
      <PageInner />
    </ApolloProviderV4>
  );
}

function PageInner() {
  const { data, loading } = useQuery(UPLOAD_PALEOMAP_LIST_PAGE);
  const paleoMapList = data?.paleoMapList?.results ?? [];

  function paleoMapOutcrops(paleoMap: PaleoMapType) {
    return R.sortBy(oc => oc.name, paleoMap.outcrops);
  }

  type PaleoMapType = NonNullable<
    NonNullable<UploadPaleomapListPageQuery['paleoMapList']>['results']
  >[number];
  type SortablePaleoMap = PaleoMapType & {
    outcropName: string | null;
    regionName: string | null;
    series: string | null;
    system: string | null;
    searchableText: string;
  };
  const paleoMapsWithMeta: SortablePaleoMap[] = paleoMapList.map(pm => {
    const topOutcrop = paleoMapOutcrops(pm).at(0);
    const outcropName = topOutcrop?.name ?? null;
    const regionName = topOutcrop?.region.name ?? null;
    const series = pm.age?.series ?? '- none set -';
    const system = pm.age?.system ?? '- none set -';

    // For searching, use the description and the outcrop names
    const descriptionText = pm.description ?? '';
    const outcropNames = paleoMapOutcrops(pm)
      .map(oc => oc.name)
      .join(';');
    const searchableText = [descriptionText, outcropNames].join(' ').trim();

    return {
      ...pm,
      outcropName,
      regionName,
      series,
      system,
      searchableText,
    };
  });

  const { items, sortIndicatorProps, filterSearchProps } = useSortFilter(
    paleoMapsWithMeta,
    'outcropName',
    'searchableText',
    'uploadPaleomapList',
  );

  if (loading) return <SpinnerPlaceholder />;

  return (
    <>
      <PageHeading>Paleomaps</PageHeading>

      <Panel>
        <Panel.Heading className="flex justify-between">
          <Panel.Heading>
            Paleomaps
            <small className="text-muted" style={{ marginLeft: '10px' }}>
              (<strong>{items.length}</strong> results displayed)
            </small>
          </Panel.Heading>

          <div className="space-x-1">
            <Link
              to={routes.uploadPaleoMapCreateRoute()}
              className="btn btn-primary btn-xs"
            >
              <FontAwesomeIcon icon={faPlus} /> Create Palaeomap
            </Link>
          </div>
        </Panel.Heading>

        <Panel.Body>
          <div className="space-y-4">
            <FilterSearch
              {...filterSearchProps}
              searchHelpText="Search by palaeomap description and outcrop names"
            />

            <table className="table table-compact w-full">
              <thead>
                <tr>
                  <th>
                    <SortTrigger
                      colName="id"
                      sortIndicatorProps={sortIndicatorProps}
                    >
                      ID
                    </SortTrigger>
                  </th>
                  <th>
                    <SortTrigger
                      colName="outcropName"
                      sortIndicatorProps={sortIndicatorProps}
                    >
                      Outcrops
                    </SortTrigger>
                  </th>
                  <th>
                    <SortTrigger
                      colName="regionName"
                      sortIndicatorProps={sortIndicatorProps}
                      filterable
                    >
                      Regions
                    </SortTrigger>
                  </th>
                  <th>Description</th>
                  <th>
                    <SortTrigger
                      colName="system"
                      sortIndicatorProps={sortIndicatorProps}
                      filterable
                    >
                      System
                    </SortTrigger>
                  </th>
                  <th>
                    <SortTrigger
                      colName="series"
                      sortIndicatorProps={sortIndicatorProps}
                      filterable
                    >
                      Series
                    </SortTrigger>
                  </th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                <NoItemsRow show={!items.length} colSpan={3} />

                {items.map(paleoMap => (
                  <tr key={paleoMap.id}>
                    <td>
                      <Link
                        to={routes.uploadPaleoMapUpdateRoute(
                          parseInt(paleoMap.id),
                        )}
                        className="link"
                      >
                        {paleoMap.id}
                      </Link>
                    </td>
                    <td>
                      {paleoMapOutcrops(paleoMap).map(outcrop => (
                        <div key={outcrop.id}>
                          <Link
                            to={routes.uploadOutcropUpdateRoute(
                              parseInt(outcrop.id),
                            )}
                            className="link"
                          >
                            {outcrop.name}
                          </Link>
                        </div>
                      ))}
                    </td>
                    <td>
                      {paleoMap.outcrops?.map(outcrop => (
                        <div key={outcrop.id}>
                          <Link
                            to={routes.uploadRegionUpdateRoute(
                              parseInt(outcrop.region.id),
                            )}
                            className="link"
                          >
                            {outcrop.region.name}
                          </Link>
                        </div>
                      ))}
                    </td>
                    <td>{paleoMap.description}</td>
                    <td>{paleoMap.age?.system}</td>
                    <td>{paleoMap.age?.series}</td>
                    <td className="text-right w-1/12">
                      <Link
                        to={routes.uploadPaleoMapUpdateRoute(
                          parseInt(paleoMap.id),
                        )}
                        className="btn btn-ghost btn-xs gap-1"
                      >
                        <FontAwesomeIcon icon={faPencil} /> Edit
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel.Body>
      </Panel>
    </>
  );
}
